import { AsyncPipe, NgClass, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { LocaleDatePipe } from '@sc/data-access-translation';
import {
  IconComponent,
  InputHourComponent,
  UtilsValidators,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { Dayjs } from 'dayjs';
import { pairwise, startWith } from 'rxjs/operators';

@Component({
  selector: 'sc-fitter-complete-date-picker',
  styleUrls: ['./fitter-complete-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    NgClass,
    IconComponent,
    MatInputModule,
    MatDatepickerModule,
    InputHourComponent,
    NgIf,
    AsyncPipe,
    TitleCasePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <ng-container [formGroup]="form">
      <div class="date-time-container">
        <div class="date">
          <mat-label
            [class.asterisk]="hasRequiredValidator(getTime())"
            class="sc-design-text-4-bold"
            >{{ 'AppointmentDatePicker.day' | translate }}</mat-label
          >
          <mat-form-field
            [ngClass]="{
              inCenter: !getOutsideIntervention()?.value,
              outsideIntervention: getOutsideIntervention()?.value
            }"
          >
            <sc-icon matPrefix name="calendar_hour"></sc-icon>
            <input
              matInput
              (focus)="inputTimeDatePicker.click()"
              (click)="inputTimeDatePicker.click()"
              [value]="
                getTime()?.value !== null
                  ? (getDateFormatted(getTime()?.value)
                    | localeDate: 'shortDate':getTimezone()
                    | async
                    | titlecase)
                  : ''
              "
              readonly
              [disabled]="disabled"
            />

            <input
              class="hidden-input"
              #inputTimeDatePicker
              matInput
              [value]="getTime()?.value"
              [matDatepicker]="timePicker"
              (focus)="timePicker.open()"
              (click)="timePicker.open()"
              (dateChange)="onTimeDateChange($event)"
              readonly
              [disabled]="disabled"
            />
            <sc-icon matSuffix name="arrow_bottom"></sc-icon>

            <mat-datepicker
              #timePicker
              panelClass="fitter-complete-date-picker-calendar"
            ></mat-datepicker>
          </mat-form-field>
        </div>
        <sc-input-hour
          formControlName="time"
          [ngClass]="{
            inCenter: !getOutsideIntervention()?.value,
            outsideIntervention: getOutsideIntervention()?.value
          }"
          [label]="'AppointmentDatePicker.start' | translate"
          [timezone]="getTimezone()"
        ></sc-input-hour>
      </div>

      <div class="date-time-container">
        <div class="date">
          <mat-label
            [class.asterisk]="hasRequiredValidator(getEndTime())"
            class="sc-design-text-4-bold"
            >{{ 'AppointmentDatePicker.day' | translate }}</mat-label
          >
          <mat-form-field
            [ngClass]="{
              inCenter: !getOutsideIntervention()?.value,
              outsideIntervention: getOutsideIntervention()?.value
            }"
          >
            <sc-icon matPrefix name="calendar_hour"></sc-icon>
            <input
              matInput
              (focus)="inputEndTimeDatePicker.click()"
              (click)="inputEndTimeDatePicker.click()"
              [value]="
                getEndTime()?.value !== null
                  ? (getDateFormatted(getEndTime()?.value)
                    | localeDate: 'shortDate':getTimezone()
                    | async
                    | titlecase)
                  : ''
              "
              readonly
              [disabled]="disabled"
            />

            <input
              class="hidden-input"
              #inputEndTimeDatePicker
              matInput
              [value]="getEndTime()?.value"
              [matDatepicker]="endTimePicker"
              (focus)="endTimePicker.open()"
              (click)="endTimePicker.open()"
              (dateChange)="onEndTimeDateChange($event)"
              readonly
              [disabled]="disabled"
              [min]="getMinEndDate()"
            />
            <sc-icon matSuffix name="arrow_bottom"></sc-icon>

            <mat-datepicker
              #endTimePicker
              panelClass="fitter-complete-date-picker-calendar"
            ></mat-datepicker>
          </mat-form-field>
        </div>
        <sc-input-hour
          formControlName="endTime"
          [ngClass]="{
            inCenter: !getOutsideIntervention()?.value,
            outsideIntervention: getOutsideIntervention()?.value
          }"
          [label]="'AppointmentDatePicker.end' | translate"
          [timezone]="getTimezone()"
        ></sc-input-hour>
      </div>

      <div class="time-error" *ngIf="touched && !areTimeSelected()">
        {{ 'AppointmentDatePicker.dateAndTimeNotFilled' | translate }}
      </div>

      <div class="time-error" *ngIf="touched && !isEndTimeValid()">
        {{ 'AppointmentDatePicker.endTimeNotValid' | translate }}
      </div>
    </ng-container>
  `,
})
export class FitterCompleteDatePickerComponent implements OnInit {
  @Input() form: FormGroup;
  @Input() disabled = false;
  @Input() addDelayToSuggestions = false;
  @Input() touched = false;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly configService: ConfigurationService
  ) {}

  ngOnInit(): void {
    let blockEndModification: boolean;

    this.getTime()
      .valueChanges.pipe(startWith<null>(null), pairwise())
      .subscribe(([prev, next]: [string, string]) => {
        if (!blockEndModification) {
          const previousTime: Dayjs = this.getMomentTime(prev);
          const nextTime: Dayjs = this.getMomentTime(next);

          const previousEndTime: Dayjs = this.getMomentTime(
            this.getEndTime()?.value
          );
          if (previousTime?.isValid() && previousEndTime?.isValid()) {
            // if everything existed, we set the endTime so the interventionDuration is not modified
            const diff = nextTime.diff(previousTime);
            this.getEndTime().setValue(previousEndTime.add(diff).format());
          } else {
            // if not, we set the endTime to the time + interventionDuration. if we don't have any interventionDuration, we take 1 hour
            this.getEndTime().setValue(
              nextTime
                .add(this.getInterventionDuration()?.value ?? 60, 'minutes')
                .format()
            );
          }
        }
      });

    this.getEndTime().valueChanges.subscribe((next: string) => {
      const previousTime = this.getMomentTime(this.getTime()?.value);
      const nextEndTime: Dayjs = this.getMomentTime(next);

      if (!previousTime?.isValid()) {
        // if there was no good formatted previousTime we set the time the interventionDuration (or 1 hour by default) before the endTime setted
        const nextTime = nextEndTime.subtract(
          this.getInterventionDuration()?.value ?? 60,
          'minutes'
        );
        blockEndModification = true;
        this.getTime().setValue(nextTime.format());
        blockEndModification = false;
      } else if (nextEndTime.isSameOrBefore(previousTime)) {
        // if the endTime isBefore the time, we add 1 day so it can be after (ex: time: 2023-11-21T11:00, previousEndTime: 2023-11-21T12:00, nextEndTime: 2023-11-21T10:00 - we change the endTime to 2023-11-22T10:00)
        this.getEndTime().setValue(nextEndTime.add(1, 'days').format());
      } else {
        // if not, everything is OK so we update the interventionDuration
        this.getInterventionDuration()?.setValue(
          nextEndTime.diff(this.getTime()?.value, 'minutes')
        );
      }
      this.changeDetectorRef.markForCheck();
    });

    this.form.get('timezone')?.valueChanges.subscribe((next: string) => {
      const time = this.getTime()?.value;
      if (dayjs(time)?.isValid())
        this.getTime().setValue(dayjs(time).tz(next, true).format());
      else {
        const endTime = this.getEndTime()?.value;
        if (dayjs(endTime)?.isValid())
          this.getEndTime().setValue(dayjs(endTime).tz(next, true).format());
      }
    });
  }

  getMomentTime(value: string): Dayjs {
    return value ? dayjs(value).tz(this.getTimezone()) : null;
  }

  getTime() {
    return this.form.get('time');
  }

  getEndTime() {
    return this.form.get('endTime');
  }

  getTimezone() {
    return (
      this.form.get('timezone')?.value ||
      this.configService.getDefaultTimezone()
    );
  }

  getOutsideIntervention() {
    return this.form.get('outsideIntervention');
  }

  getInterventionDuration() {
    return this.form.get('interventionDuration');
  }

  getDateFormatted(date: string) {
    return dayjs(date).toISOString();
  }

  getMinEndDate() {
    const time = this.getMomentTime(this.getTime()?.value);
    const endTime = this.getMomentTime(this.getEndTime()?.value);
    if (!time?.isValid() || !endTime?.isValid()) return;

    const endFictif = time
      .clone()
      .hour(endTime.hour())
      .minute(endTime.minute());

    let minEndDate = time.clone();
    if (endFictif.isSameOrBefore(time)) {
      minEndDate = minEndDate.add(1, 'day');
    }
    return minEndDate;
  }

  onTimeDateChange($event) {
    let previousTimeDate: Dayjs = this.getMomentTime(this.getTime()?.value);
    // To choose the hour, we take the previous time if existing. if not, we select the next hour (10h16 => 11h00)
    previousTimeDate = previousTimeDate?.isValid()
      ? previousTimeDate
      : dayjs().tz(this.getTimezone()).minute(0).add(1, 'hours');
    const timeDateToSave: Dayjs = dayjs($event?.value)
      .tz(this.getTimezone())
      .hour(previousTimeDate.hour())
      .minute(previousTimeDate.minute());
    this.getTime().setValue(timeDateToSave.format());
  }

  onEndTimeDateChange($event) {
    const previousEndTime: Dayjs = this.getMomentTime(this.getEndTime()?.value);

    if (previousEndTime?.isValid()) {
      // if the previousEndTime is valid, we can keep the previous HH:mm
      const endTimeDateToSave: Dayjs = dayjs($event?.value)
        .tz(this.getTimezone())
        .hour(previousEndTime.hour())
        .minute(previousEndTime.minute());

      this.getEndTime().setValue(endTimeDateToSave.format());
    } else {
      // if we had no endTime, we will use the time to define the hours of endTime. the new time will automatically update the endTime (in valueChanges)
      const time: Dayjs = this.getMomentTime(this.getTime()?.value);

      if (time?.isValid()) {
        // if we had a time but no endTime, we choose the nextEndTime according to it
        const nextEndTime = dayjs($event?.value)
          .tz(this.getTimezone())
          .hour(time.hour())
          .minute(time.minute())
          .add(this.getInterventionDuration()?.value ?? 60, 'minutes');

        this.getEndTime().setValue(nextEndTime.format());
      } else {
        // if we had no time nor endTime, we select the nextHour as time, in the day asked. The valueChanges will also change the endTime so it is the same day asked
        const actualTime = dayjs().tz(this.getTimezone());

        let nextTime = dayjs($event?.value)
          .tz(this.getTimezone())
          .hour((actualTime?.hour() + 1) % 24)
          .minute(0)
          .second(0)
          .millisecond(0) as Dayjs;

        // The definition of time could put the endTime the day after the one asked so we make sure it doesn't
        if (
          nextTime
            .add(this.getInterventionDuration()?.value ?? 60, 'minutes')
            .isAfter(nextTime, 'day')
        ) {
          nextTime = nextTime.subtract(1, 'day');
        }
        this.getTime().setValue(nextTime.format());
      }
    }
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }

  areTimeSelected() {
    return (
      (!this.hasRequiredValidator(this.getTime()) || this.getTime()?.value) &&
      (!this.hasRequiredValidator(this.getEndTime()) ||
        this.getEndTime()?.value)
    );
  }

  isEndTimeValid() {
    const time = this.getMomentTime(this.getTime()?.value);
    const endTime = this.getMomentTime(this.getEndTime()?.value);
    return (
      !!time &&
      time?.isValid() &&
      !!endTime &&
      endTime?.isValid() &&
      time.isBefore(endTime)
    );
  }
}
